/**
 * ----------------------------------------------------------------------------
 * Nonconformity Store
 * ----------------------------------------------------------------------------
 * Summary:
 * MobX store for managing nonconformity records, including fetching,
 * filtering, adding, updating, deleting, pagination, computed grid data,
 * and resetting operation states.
 *
 * @author Chandan Singh
 * @created August-2026
 * ----------------------------------------------------------------------------
 */

import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  INonConformanceList,
  INonConformanceListVM,
  INonConformanceVM,
} from '../../models/response/INonconformityResponse';
import {
  IAddNonConformance,
  IUpdateNonConformance,
} from '../../models/forms/INonconformity';
import { INonconformityState } from '../../models/state/INonconformityState';
import { formatMessage } from '../../translations/format-message';
import {
  GetAllNonConformanceState,
  GetNonConformityState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import {
  IAddNCRequest,
  IAddNGRequest,
  IApproveNCRequest,
  ISubmitNCWork,
} from '../../models/forms/IAddNonConformity';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { IGetNonConformance } from '../../models/response/IAddedNonConformanceResponse';
import { emptyValue, fixTimezoneOffsetYYYYMMDD, minDate } from '../../constants/constants';
import { IProcessListVM } from '../../models/response/IProcessResponse';
import { GetAllProcessState } from '../initialState';
import { IOption } from '../../models/ICommon';

interface IColumnProps {
  Id?: number;
  ShortName: string;
  IsHidden: boolean;
}

export class NonconformityStore implements INonconformityState {
  initialStateValue = ObservableInitialState;

  NonConformanceResponseList: INonConformanceListVM = GetAllNonConformanceState;
  nonConformance: INonConformanceVM | undefined = undefined;
  nonConformanceReportState = { ...this.initialStateValue };
  nonConformanceReport: any | undefined = undefined;
  processList: any = GetAllProcessState;
  checkedColumns: any = [];
  subContractorList: any = [];
  itemList: any = [];
  listViewList: any = [];
  byProcessChartData: any = [];
  subContractorChartData: any = [];
  itemChartData: any = [];
  nonconformitySummary: any = []

  getAllNonConformanceState = { ...this.initialStateValue };
  addUpdateNonConformanceState = { ...this.initialStateValue };
  deleteNonConformanceState = { ...this.initialStateValue };
  addNCRequestState = { ...this.initialStateValue };
  getNonConformanceByIdState = { ...this.initialStateValue };
  approveNonConformityState = { ...this.initialStateValue };
  verifyNonConformityState = { ...this.initialStateValue };
  nonConformity: IGetNonConformance | undefined = undefined;
  submitNonConformanceWorkState = { ...this.initialStateValue };
  exportNCReportState = { ...this.initialStateValue };
  base64String: any = { fileString: '' };
  NCReportChartState = { ...this.initialStateValue };
  getColumnsListState = { ...this.initialStateValue };
  nonConformitySummaryState = { ...this.initialStateValue }

  constructor() {
    makeObservable(this, {
      NonConformanceResponseList: observable,
      nonConformance: observable,
      getAllNonConformanceState: observable,
      addUpdateNonConformanceState: observable,
      deleteNonConformanceState: observable,
      addNCRequestState: observable,
      getNonConformanceByIdState: observable,
      nonConformity: observable,
      approveNonConformityState: observable,
      submitNonConformanceWorkState: observable,
      verifyNonConformityState: observable,
      nonConformanceReportState: observable,
      nonConformanceReport: observable,
      base64String: observable,
      NCReportChartState: observable,
      processList: observable,
      checkedColumns: observable,
      getColumnsListState: observable,
      exportNCReportState: observable,
      subContractorList: observable,
      itemList: observable,
      listViewList: observable,
      byProcessChartData: observable,
      subContractorChartData: observable,
      itemChartData: observable,
      nonconformitySummary: observable,
      nonConformitySummaryState: observable,

      GetAllNonConformanceService: action,
      AddNonConformanceService: action,
      UpdateNonConformanceService: action,
      DeleteNonConformanceService: action,
      AddNCRequestService: action,
      GetNonConformanceByIdService: action,
      ApproveNonConformanceService: action,
      SubmitNonConformanceWork: action,
      VerifyNonConformanceService: action,
      GetNonConformanceReportService: action,
      GetNCReportByProcess: action,
      GetAllProcessService: action,
      GetSubContractorListService: action,
      GetItemListService: action,
      GetChartDataByProcess: action,
      GetBySubContractorReport: action,
      GetByItemsReport: action,
      UpdateColumnSelection: action,
      GetNonConformitySummaryService: action,

      resetAddUpdateNonConformanceState: action,
      resetDeleteNonConformanceState: action,
      reset: action,
      resetAddNCRequestState: action,
      resetGetNonConformanceByIdState: action,
      resetApproveNonConformityState: action,
      resetVerifyNonConformanceState: action,
      resetExportNCReportState: action,
      resetNCReportChartState: action,
      resetNonConformity: action,

      allNonConformance: computed,
      reportedNonConformity: computed,
      allProcessOptions: computed,
      getSubContractorListOptions: computed,
      getItemListOptions: computed,
      getListViewList: computed,
      getByProcessCheckedValues: computed,
      getSubContractorCheckedValues: computed,
      getitemChartCheckedValues: computed,
      getNonconformitySummaryDetails: computed,
      resetStore: action,
    });
  }

  //#region Actions
  GetAllNonConformanceService = (
    currentPage: number,
    pagerSize: number,
    column?: string,
    order?: string,
    status?: number
  ) => {
    this.getAllNonConformanceState.inProgress = true;
    this.NonConformanceResponseList = GetAllNonConformanceState
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      PageNumber: currentPage,
      PageSize: pagerSize,
      Column: column || '',
      Order: order || '',
      Status: status !== undefined ? status : -1,
      TrayId: '',
      Katashiki: '',
      // ProcessId: 0,
      SubContractor: '',
    };

    // replace with real API call once mapping is confirmed:
    const url =
      URLConstants.GetAllNonConformanceList
    return baseService
      .postRequest(url, postData)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<INonConformanceListVM>>
        ) => {
          if (response.data.Error) {
            this.getAllNonConformanceState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.NonConformanceResponseList = response.data.Data;
            this.getAllNonConformanceState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getAllNonConformanceState.error = err;
      })
      .finally(
        action(() => {
          this.getAllNonConformanceState.inProgress = false;
        })
      );

    //   return Promise.resolve()
    //     .then(
    //       action(() => {
    //         const fullList = (dummyResponse as any)
    //           .NonConformanceResponseList as INonConformanceList[];

    //         const filtered =
    //           status !== undefined
    //             ? fullList.filter((nc: any) => nc.Status === status)
    //             : fullList;

    //         const startIndex = (currentPage - 1) * pagerSize;
    //         const pagedItems = filtered.slice(startIndex, startIndex + pagerSize);

    //         this.NonConformanceResponseList = {
    //           NonConformanceResponseList: pagedItems,
    //           PagingDetails: {
    //             TotalRecords: filtered.length,
    //             TotalPages: Math.max(1, Math.ceil(filtered.length / pagerSize)),
    //             CurrentPage: currentPage,
    //             PageSize: pagerSize,
    //           },
    //         } as unknown as INonConformanceListVM;
    //         this.getAllNonConformanceState.success = true;
    //       })
    //     )
    //     .finally(
    //       action(() => {
    //         this.getAllNonConformanceState.inProgress = false;
    //       })
    //     );
  };

  AddNonConformanceService = (data: IAddNonConformance) => {
    this.addUpdateNonConformanceState.inProgress = true;
    const url = URLConstants.AddNonConformance;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateNonConformanceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addUpdateNonConformanceState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateNonConformanceState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateNonConformanceState.inProgress = false;
        })
      );
  };

  UpdateNonConformanceService = (data: IUpdateNonConformance) => {
    this.addUpdateNonConformanceState.inProgress = true;
    const url = URLConstants.UpdateNonConformance;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateNonConformanceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addUpdateNonConformanceState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateNonConformanceState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateNonConformanceState.inProgress = false;
        })
      );
  };

  DeleteNonConformanceService = (id: number) => {
    this.deleteNonConformanceState.inProgress = true;
    const url = URLConstants.DeleteNonConformance + '/' + id;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteNonConformanceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteNonConformanceState.success = true;
        }
      })
      .catch((err: string) => {
        this.deleteNonConformanceState.error = err;
      })
      .finally(
        action(() => {
          this.deleteNonConformanceState.inProgress = false;
        })
      );
  };

  AddNCRequestService = (data: IAddNCRequest) => {
    this.addNCRequestState.inProgress = true;
    const url = URLConstants.AddNCRequest;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addNCRequestState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addNCRequestState.success = true;
        }
      })
      .catch((err: string) => {
        this.addNCRequestState.error = err;
      })
      .finally(
        action(() => {
          this.addNCRequestState.inProgress = false;
        })
      );
  };

  GetNonConformanceByIdService = (tenantId: number, id: number) => {
    this.getNonConformanceByIdState.inProgress = true;
    let url = `${URLConstants.GetNonConformanceById}/${tenantId}?NonConformanceId=${id}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IGetNonConformance>>) => {
          if (response.data.Error) {
            this.getNonConformanceByIdState.error = response.data.Message;
          } else {
            this.nonConformity = response.data.Data;
            this.getNonConformanceByIdState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getNonConformanceByIdState.error = err;
      })
      .finally(
        action(() => {
          this.getNonConformanceByIdState.inProgress = false;
        })
      );
  };

  ApproveNonConformanceService = (data: IApproveNCRequest) => {
    this.approveNonConformityState.inProgress = true;
    const url = URLConstants.ApproveNCRequest;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.approveNonConformityState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.approveNonConformityState.success = true;
        }
      })
      .catch((err: string) => {
        this.approveNonConformityState.error = err;
      })
      .finally(
        action(() => {
          this.approveNonConformityState.inProgress = false;
        })
      );
  };

  SubmitNonConformanceWork = (data: ISubmitNCWork) => {
    this.submitNonConformanceWorkState.inProgress = true;
    const url = URLConstants.NonConformanceWork;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.submitNonConformanceWorkState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.submitNonConformanceWorkState.success = true;
        }
      })
      .catch((err: string) => {
        this.submitNonConformanceWorkState.error = err;
      })
      .finally(
        action(() => {
          this.submitNonConformanceWorkState.inProgress = false;
        })
      );
  };

  VerifyNonConformanceService = (data: IAddNGRequest) => {
    this.verifyNonConformityState.inProgress = true;
    const url = URLConstants.VerifyNCRequest;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.verifyNonConformityState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.verifyNonConformityState.success = true;
        }
      })
      .catch((err: string) => {
        this.verifyNonConformityState.error = err;
      })
      .finally(
        action(() => {
          this.verifyNonConformityState.inProgress = false;
        })
      );
  };

  GetNonConformanceReportService = (
    startDateTime: string,
    endDateTime: string,
    currentPage: number,
    pagerSize: number,
    column?: string,
    order?: string
  ) => {
    this.nonConformanceReportState.inProgress = true;
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      StartDateTime: startDateTime,
      EndDateTime: endDateTime,
      PageNumber: currentPage,
      PageSize: pagerSize,
      Column: column || '',
      Order: order || '',
    };

    const url = URLConstants.GetNonconformanceReport;
    return baseService
      .postRequest(url, postData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.nonConformanceReportState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.listViewList = response.data.Data;
          this.nonConformanceReportState.success = true;
        }
      })
      .catch((err: string) => {
        this.nonConformanceReportState.error = err;
      })
      .finally(
        action(() => {
          this.nonConformanceReportState.inProgress = false;
        })
      );
  };

  DownloadNCReportCSV = (startDateTime: string, endDateTime: string) => {
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      StartDateTime: startDateTime,
      EndDateTime: endDateTime,
      ByProcess: 0,
      SubContractor: 0,
      Items: '',
    };
    this.exportNCReportState.inProgress = true;
    let url = URLConstants.ExportNCReport;
    return baseService
      .postRequest(url, postData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.exportNCReportState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.exportNCReportState.success = true;
          this.base64String = { fileString: response.data.Data };
        }
      })
      .catch((err: string) => {
        this.exportNCReportState.error = err;
      })
      .finally(
        action(() => {
          this.exportNCReportState.inProgress = false;
        })
      );
  };

  GetNCReportByProcess = (startDateTime: string, endDateTime: string) => {
    this.NCReportChartState.inProgress = true;
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      StartDateTime: startDateTime,
      EndDateTime: endDateTime,
    };

    const url = URLConstants.GetNCReportByProcess;
    return baseService
      .postRequest(url, postData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.NCReportChartState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.NCReportChartState.success = true;
        }
      })
      .catch((err: string) => {
        this.NCReportChartState.error = err;
      })
      .finally(
        action(() => {
          this.NCReportChartState.inProgress = false;
        })
      );
  };

  GetAllProcessService = () => {
    // const ProcessOrder = processOrder === 'null' ? '' : processOrder;
    this.getColumnsListState.inProgress = true;
    let url = URLConstants.GetAllProcess;
    this.checkedColumns = []
    // if (tenentId !== undefined) {
    //   url += `?tenantId=${tenentId}`;
    // }
    // if (HideSettingKey !== undefined) {
    //   url += `${tenentId !== undefined ? '&' : '?'}HideSettingKey=${HideSettingKey}`;
    // }
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IProcessListVM>>) => {
        if (response.data.Error) {
          this.getColumnsListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.processList = response.data.Data;
          this.checkedColumns = this.processList.Processs.map(
            (process: any) => process.Id
          );
          this.getColumnsListState.success = true;
        }
      })
      .catch((err: string) => {
        this.getColumnsListState.error = err;
      })
      .finally(
        action(() => {
          this.getColumnsListState.inProgress = false;
        })
      );
  };

  GetSubContractorListService = () => {
    this.getColumnsListState.inProgress = true;
    const tenantId = Number(getTenantIdFromLocalStorage());
    const url = `${URLConstants.GetSubContractorList}/${tenantId}`;
    this.checkedColumns = []
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getColumnsListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.subContractorList = response.data.Data.NCSubContractorList;
          this.checkedColumns = this.subContractorList.map(
            (process: any) => process.SubContractorName
          );
          this.getColumnsListState.success = true;
        }
      })
      .catch((err: string) => {
        this.getColumnsListState.error = err;
      })
      .finally(
        action(() => {
          this.getColumnsListState.inProgress = false;
        })
      );
  };

  GetItemListService = () => {
    this.getColumnsListState.inProgress = true;
    const tenantId = Number(getTenantIdFromLocalStorage());
    const url = URLConstants.GetItemsBySubjectList;
    this.checkedColumns = []
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getColumnsListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.itemList = response.data.Data;
          this.checkedColumns = this.itemList;
          this.getColumnsListState.success = true;
        }
      })
      .catch((err: string) => {
        this.getColumnsListState.error = err;
      })
      .finally(
        action(() => {
          this.getColumnsListState.inProgress = false;
        })
      );
  };

  GetChartDataByProcess = (
    startDateTime: string,
    endDateTime: string,
    ProcessIds: number[]
  ) => {
    this.NCReportChartState.inProgress = true;
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      StartDateTime: startDateTime,
      EndDateTime: endDateTime,
      ProcessIds: ProcessIds,
    };

    const url = URLConstants.GetByProcessReport;
    return baseService
      .postRequest(url, postData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.NCReportChartState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.byProcessChartData = response.data.Data;
          this.NCReportChartState.success = true;
        }
      })
      .catch((err: string) => {
        this.NCReportChartState.error = err;
      })
      .finally(
        action(() => {
          this.NCReportChartState.inProgress = false;
        })
      );
  };

  GetBySubContractorReport = (
    startDateTime: string,
    endDateTime: string,
    SubContractors: string[]
  ) => {
    this.NCReportChartState.inProgress = true;
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      SubContractors: SubContractors,
      StartDateTime: startDateTime,
      EndDateTime: endDateTime,
    };

    const url = URLConstants.GetBySubContractorReport;
    return baseService
      .postRequest(url, postData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.NCReportChartState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.subContractorChartData = response.data.Data;
          this.NCReportChartState.success = true;
        }
      })
      .catch((err: string) => {
        this.NCReportChartState.error = err;
      })
      .finally(
        action(() => {
          this.NCReportChartState.inProgress = false;
        })
      );
  };

  GetByItemsReport = (
    startDateTime: string,
    endDateTime: string,
    Items: string[]
  ) => {
    this.NCReportChartState.inProgress = true;
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      Items: Items,
      StartDateTime: startDateTime,
      EndDateTime: endDateTime,
    };

    const url = URLConstants.GetByItemsReport;
    return baseService
      .postRequest(url, postData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.NCReportChartState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.itemChartData = response.data.Data;
          this.NCReportChartState.success = true;
        }
      })
      .catch((err: string) => {
        this.NCReportChartState.error = err;
      })
      .finally(
        action(() => {
          this.NCReportChartState.inProgress = false;
        })
      );
  };

  UpdateColumnSelection = (selectedValues: (number | string)[]) => {
    this.checkedColumns = selectedValues;
  };

  GetNonConformitySummaryService = (StartDate: string, Endate: string) => {
    this.nonConformitySummaryState.inProgress = true;
    const postData = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      StartDateTime: StartDate,
      EndDateTime: Endate,
    };

    const url = URLConstants.GetNonconformitySummary;
    return baseService
      .postRequest(url, postData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.nonConformitySummaryState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.nonconformitySummary = response.data.Data;
          this.nonConformitySummaryState.success = true;
        }
      })
      .catch((err: string) => {
        this.nonConformitySummaryState.error = err;
      })
      .finally(
        action(() => {
          this.nonConformitySummaryState.inProgress = false;
        })
      );

  }
  //#endregion

  //#region Computed
  get allNonConformance(): INonConformanceList[] {
    let ncData: INonConformanceList[] = [];
    if (
      this.NonConformanceResponseList?.NonConformanceResponseList &&
      this.NonConformanceResponseList.NonConformanceResponseList.length > 0
    ) {
      this.NonConformanceResponseList.NonConformanceResponseList.map((nc) => {
        ncData.push({
          ...nc,
          Id: nc.Id,
          TrayId: nc.TrayId,
          TrayName: nc.TrayName ?? emptyValue,
          DetectionProcessName: nc.DetectionProcessName ?? emptyValue,
          ReturProcessName: nc.ReturProcessName ?? emptyValue,
          DetectedByName: nc.DetectedByName ?? emptyValue,
          DetectedOn: fixTimezoneOffsetYYYYMMDD(nc.DetectedOn) ?? emptyValue,
          Katashiki: nc.Katashiki ?? emptyValue,
          Kishu: nc.Kishu ?? emptyValue,
          Status: nc.Status,
          Worker: nc.Worker || emptyValue,
          WorkingDays:
            !nc.WorkingDays || nc.WorkingDays === minDate
              ? emptyValue
              : fixTimezoneOffsetYYYYMMDD(nc.WorkingDays),
          SubContractorName: nc.SubContractorName || emptyValue,
          CorrectionTime: nc.CorrectionTime,
          NonConformanceItems: nc.NonConformanceItems,
        });
      });
    }
    return ncData;
  }

  get reportedNonConformity(): IGetNonConformance {
    if (this.nonConformity) {
      return {
        NCNGDetailModelItems: this.nonConformity.NCNGDetailModelItems,
        Id: this.nonConformity.Id,
        TrayId: this.nonConformity.TrayId,
        Tray: this.nonConformity.Tray,
        DetectionProcessId: this.nonConformity.DetectionProcessId,
        DetectionProcessName: this.nonConformity.DetectionProcessName,
        ReturnProcessId: this.nonConformity.ReturnProcessId,
        ReturnProcessName: this.nonConformity.ReturnProcessName,
        DetectedBy: this.nonConformity.DetectedBy,
        DetectedByName: this.nonConformity.DetectedByName,
        DetectedOn: this.nonConformity.DetectedOn,
        WorkingDays: this.nonConformity.WorkingDays,
        Katashiki: this.nonConformity.Katashiki,
        Kishu: this.nonConformity.Kishu,
        worker: this.nonConformity.worker,
        SubContractor: this.nonConformity.SubContractor,
        SubContractorName: this.nonConformity.SubContractorName,
        Status: this.nonConformity.Status,
        NCApprovedBy: this.nonConformity.NCApprovedBy,
        NCApprovedByName: this.nonConformity.NCApprovedByName,
        NCApprovedOn: this.nonConformity.NCApprovedOn,
        WorkApprovedBy: this.nonConformity.WorkApprovedBy,
        WorkApprovedByName: this.nonConformity.WorkApprovedByName,
        WorkApprovedOn: this.nonConformity.WorkApprovedOn,
        CorrectionTime: this.nonConformity.CorrectionTime,
        NonConformanceItems: this.nonConformity.NonConformanceItems,
        ProductId: this.nonConformity.ProductId,
        Quantity: this.nonConformity.Quantity,
      };
    }
    return {
      Id: -1,
      ...GetNonConformityState,
      NonConformanceItems: [],
      NCNGDetailModelItems: [],
    };
  }

  get allProcessOptions(): IOption[] {
    let processData: IOption[] = [];
    // processData.push({
    //   id: -1,
    //   value: 'please_select',
    //   disabled: false,
    // });
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      this.processList.Processs.map((process) => {
        processData.push({
          id: process.Id,
          value: process.ProcessName,
        });
      });
    }
    return processData;
  }

  get getSubContractorListOptions(): IOption[] {
    if (!this.processList || this.processList.length === 0) {
      return [];
    }

    return this.subContractorList.map((subContractor) => ({
      id: subContractor.SubContractor,
      value: subContractor.SubContractorName,
    }));
  }

  get getItemListOptions(): IOption[] {
    if (!this.itemList || this.itemList.length === 0) {
      return [];
    }

    return this.itemList.map((item, index) => ({
      id: index + 1,
      value: item,
    }));
  }

  get getListViewList(): INonConformanceList[] {
    if (
      this.listViewList?.NonConformanceResponseList &&
      this.listViewList.NonConformanceResponseList.length > 0
    ) {
      return this.listViewList.NonConformanceResponseList.map((item) => {
        return {
          ...item,
          DetectionDate: fixTimezoneOffsetYYYYMMDD(item.DetectedOn) ?? emptyValue,
          DetectionProcess: item.DetectionProcessName ?? emptyValue,
          Detector: item.DetectedByName ?? emptyValue,
          WorkingDays:
            !item.WorkingDays || item.WorkingDays === minDate
              ? emptyValue
              : fixTimezoneOffsetYYYYMMDD(item.WorkingDays),
          WorkProcess: item.ReturProcessName ?? emptyValue,
          SubContractor: item.SubContractorName || emptyValue,
          Worker: item.Worker || emptyValue,
          TrayId: item.TrayId,
          Kishu: item.Kishu.trim() || emptyValue,
          Katashiki: item.Katashiki.trim() || emptyValue,
          NoOfDefectiveItems: item.NonConformanceItems.length,
          CorrectionTime: item.CorrectionTime,
          Id: item.Id,
        };
      });
    } else {
      return [];
    }
  }

  get getByProcessCheckedValues(): number[] {
    if (this.checkedColumns && this.checkedColumns.length > 0) {
      return this.checkedColumns;
    }
    return [];
  }
  get getSubContractorCheckedValues(): string[] {
    if (this.checkedColumns && this.checkedColumns.length > 0) {
      return this.checkedColumns;
    }
    return [];
  }

  get getitemChartCheckedValues(): string[] {
    if (this.checkedColumns && this.checkedColumns.length > 0) {
      return this.checkedColumns;
    }
    return [];
  }

  get getNonconformitySummaryDetails(): any {
    const s = this.nonconformitySummary;
    const completed = s?.CompletedCount || 0;
    const reported = s?.ReportedCount || 0;
    const approvalPending = s?.ApprovalPendingCount || 0;
    const approved = s?.VerificationCount || 0;

    const total = s?.ReportedCount;

    const getPercent = (value: number) =>
      total > 0 ? Math.round((value / total) * 100) : 0;

    return [
      {
        labelId: "nc_total_trays",//pending
        value: reported,
        percent: getPercent(reported),
        variant: "nc-stat-completed"
      },
      {
        labelId: "nc_completed_trays",//completed
        value: completed,
        percent: getPercent(completed),
        variant: "nc-stat-total"
      },
      {
        labelId: "nc_waiting_for_approval_trays",//waiting for approval
        value: approvalPending,
        percent: getPercent(approvalPending),
        variant: "nc-stat-pending"
      },
      {
        labelId: "nc_waiting_for_verification_trays",//waiting for verificaion
        value: approved,
        percent: getPercent(approved),
        variant: "nc-stat-reported"
      },
    ];
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.NonConformanceResponseList = GetAllNonConformanceState;
  };

  resetAddUpdateNonConformanceState = () => {
    this.addUpdateNonConformanceState.inProgress = false;
    this.addUpdateNonConformanceState.success = false;
    this.addUpdateNonConformanceState.error = '';
  };

  resetDeleteNonConformanceState = () => {
    this.deleteNonConformanceState = { ...this.initialStateValue };
  };

  resetAddNCRequestState = () => {
    this.addNCRequestState = { ...this.initialStateValue };
  };

  resetGetNonConformanceByIdState = () => {
    this.getNonConformanceByIdState = { ...this.initialStateValue };
  };

  resetNonConformity = () => {
    this.nonConformity = undefined;
    this.getNonConformanceByIdState = { ...this.initialStateValue };
    this.addNCRequestState = { ...this.initialStateValue };
    this.addUpdateNonConformanceState = { ...this.initialStateValue };
    this.approveNonConformityState = { ...this.initialStateValue };
    this.submitNonConformanceWorkState = { ...this.initialStateValue };
    this.verifyNonConformityState = { ...this.initialStateValue };
  };

  resetApproveNonConformityState = () => {
    this.approveNonConformityState = { ...this.initialStateValue };
  };

  resetVerifyNonConformanceState = () => {
    this.verifyNonConformityState = { ...this.initialStateValue };
  };

  resetSubmitNCWorkState = () => {
    this.submitNonConformanceWorkState = { ...this.initialStateValue };
  };

  resetNonConformanceReportState = () => {
    this.nonConformanceReportState = { ...this.initialStateValue };
  };

  resetExportNCReportState = () => {
    this.exportNCReportState = { ...this.initialStateValue };
    // this.base64String = undefined;
  };

  resetGetColumnsListState = () => {
    this.getColumnsListState = { ...this.initialStateValue };
  };

  resetNCReportChartState = () => {
    this.NCReportChartState = { ...this.initialStateValue };
    this.byProcessChartData = [];
    this.subContractorChartData = [];
    this.itemChartData = [];
    this.checkedColumns = [];
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.NonConformanceResponseList = GetAllNonConformanceState;
    this.nonConformance = undefined;
    this.getAllNonConformanceState = { ...this.initialStateValue };
    this.addUpdateNonConformanceState = { ...this.initialStateValue };
    this.deleteNonConformanceState = { ...this.initialStateValue };
    this.addNCRequestState = { ...this.initialStateValue };
    this.getNonConformanceByIdState = { ...this.initialStateValue };
    this.nonConformity = undefined;
    this.approveNonConformityState = { ...this.initialStateValue };
    this.submitNonConformanceWorkState = { ...this.initialStateValue };
    this.verifyNonConformityState = { ...this.initialStateValue };
    this.nonConformanceReportState = { ...this.initialStateValue };
    this.nonConformanceReport = undefined;
    this.base64String = { fileString: '' };
    this.NCReportChartState = { ...this.initialStateValue };
    this.processList = GetAllProcessState;
    this.checkedColumns = [];
    this.getColumnsListState = { ...this.initialStateValue };
    this.exportNCReportState = { ...this.initialStateValue };
    this.subContractorList = [];
    this.itemList = [];
    this.listViewList = [];
    this.byProcessChartData = [];
    this.subContractorChartData = [];
    this.itemChartData = [];
  };
  //#endregion
}

export default new NonconformityStore();
